import { resendVerificationSchema } from "@closer/shared";
import { maskEmail } from "@closer/shared";
import { Button, FormField, Input, Spinner, useToast } from "@closer/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { z } from "zod";

import { MailIcon } from "../../components/icons";
import { AuthLayout } from "../../layouts/AuthLayout";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { guvenliDonusYolu, ISTENEN_DONUS_PARAM } from "../../lib/returnTo";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";
import type { InvitationInbox } from "../invitations/MyInvitationsPage";

type ResendValues = z.infer<typeof resendVerificationSchema>;

/**
 * E-posta doğrulama ekranı (CLOS-359 `[S05]`).
 *
 * Üç durum, tek sayfa:
 * - **doğrulanıyor** — bağlantıyla gelindi, sunucudan cevap bekleniyor
 * - **başarılı** — hesap doğrulandı
 * - **bekliyor** — token yok ya da geçersiz/süresi dolmuş; yeniden gönderim yolu açılır
 *
 * Ayrı bir "süresi dolmuş" ekranı YOK ve bu bilinçli: kullanıcı için "bağlantı çalışmadı"
 * ile "bağlantı eskimiş" arasındaki fark eylemi değiştirmez — ikisinde de yapılacak şey
 * yeni bağlantı istemektir. Ayrıca sunucu da bu durumları AYIRT ETMİYOR (elinde token
 * olan birine hesap durumu sızmasın diye); arayüzün ayırt ediyormuş gibi davranması o
 * kararı boşa çıkarırdı.
 */
/**
 * ⚠ BU EKRANDA SAYAÇ YOK — ve bu bir eksiklik değil, ölçüldü.
 *
 * Davet ekranıyla tutarlılık için bir bekleme sayacı eklendi, sonra GERİ ALINDI: gönderim
 * başarılı olunca bu ekran formu tamamen kapatıp onay metnine geçiyor (`gonderimMesaji`
 * dalı). Yani sayaç, hiç görünmeyecek bir düğmenin üstünde yaşayacaktı — ulaşılamaz kod.
 *
 * Tutarsızlığın GERÇEK yarısı adresti (davet ekranı maskeliyor, burası düz gösteriyordu)
 * ve o düzeltildi. İki ekranın farkı bilinçli: burada form kapanıyor, davet ekranında
 * kullanıcı davetin yanında kalıyor.
 */

type Durum = "dogrulaniyor" | "basarili" | "bekliyor";

/**
 * Doğrulama ekranından çıkış (CLOS-496 Hata düzeltmesi).
 *
 * Bu ekran iki farklı kullanıcıya açılıyor ve "geri dön" ikisi için farklı anlama
 * geliyor:
 *
 * - **Oturumsuz** (e-postadaki bağlantıyla gelen): giriş sayfasına gitmek doğru → Link.
 * - **Girişli ama doğrulanmamış** (kayıt sonrası, `RequireVerifiedEmail` buraya getirdi):
 *   "Giriş sayfasına dön" /login'e, /login onu /tenants'a, `RequireVerifiedEmail` yeniden
 *   /verify-email'e atıyordu — sonsuz döngü, buton "çalışmıyor" görünüyordu. Bu
 *   kullanıcının tek anlamlı çıkışı OTURUMU KAPATMAK; çıkınca /login'de kalır.
 */
function GeriDon({ className }: { className?: string }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Oturumsuz (e-postadaki bağlantıyla gelen): giriş sayfası doğru hedef.
  if (!user) {
    return (
      <Link
        to="/login"
        className={className ?? "text-center font-medium text-primary hover:underline"}
      >
        Giriş sayfasına dön
      </Link>
    );
  }

  // Girişli (doğrulanmamış): GeriDon yalnız "bekliyor" durumunda kullanılıyor; orada
  // kullanıcı tanımı gereği doğrulanmamış. "Giriş sayfasına dön" /login → /tenants →
  // RequireVerifiedEmail → /verify-email döngüsü yapıyordu (CLOS-496). Tek anlamlı
  // çıkış oturumu kapatmak; çıkınca /login'de kalır. (Doğrulama başarılı durumu GeriDon
  // KULLANMIYOR — orası ayrı ele alınıyor, session'a bakmıyor.)
  const cikisYap = async (): Promise<void> => {
    try {
      await logout();
      navigate("/login");
    } catch {
      toast.error("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  return (
    <Button variant="ghost" onClick={() => void cikisYap()}>
      Çıkış yap
    </Button>
  );
}

export function VerifyEmailPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [durum, setDurum] = useState<Durum>(token ? "dogrulaniyor" : "bekliyor");
  const [gonderimMesaji, setGonderimMesaji] = useState<string | null>(null);
  const [bekleyenHedef, setBekleyenHedef] = useState<string | null>(null);

  /**
   * Doğrulama BAŞARILI olduktan sonra ileri gitme.
   *
   * Doğrulamanın başarısı POST 200 ile KESİN; oturumdaki `emailVerified` bayrağı henüz
   * bayat olabilir (ekranı bloklamamak için render anında tazelemiyoruz). Bu yüzden
   * GeriDon'un session'a bakan mantığına GÜVENMEK yerine burada net karar veriyoruz:
   * girişliyse oturumu tazeleyip uygulamaya sok (tazelemeden /tenants'a gitsek
   * RequireVerifiedEmail bayat bayrağı görüp /verify-email'e geri atardı), oturumsuzsa
   * giriş sayfasına.
   */
  const uygulamayaDevam = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["session"] });
    /**
     * ⚠ DÖNÜŞ HEDEFİ İZİN LİSTESİNDEN GEÇİYOR (FAZ 0A, madde 6).
     *
     * Doğrulanmamış e-postalı kullanıcı temsilci davetinde ölü uca düşüyordu: davet
     * ekranı "önce doğrulayın" diyor, bu sayfa ise bitince HER HÂLDE `/tenants`'a
     * atıyordu — kullanıcı davete bir daha dönemiyordu. Hedef artık taşınıyor.
     *
     * ⚠ Taşınan her hedef bir açık yönlendirme adayıdır; `guvenliDonusYolu` yalnız
     * bilinen davet rotalarını kabul ediyor, gerisi bu varsayılana düşüyor.
     *
     * ⚠ TAZELEME DÖNÜŞTEN ÖNCE: davete bayat `emailVerified=false` ile dönseydik ekran
     * yine "önce doğrulayın" derdi ve kullanıcı aynı ölü uca ikinci kez düşerdi.
     *
     * ⚠ AMA `await` TEK BAŞINA YETMİYOR (ölçüldü): tazeleme çözüldüğünde veri ÖNBELLEKTE
     * hazır, fakat React Query abonelere bildirimi AYRI bir turda yolluyor — bu satırda
     * ağaç HÂLÂ bayat. Hemen gidersek hedef ekran bir kare "önce doğrulayın" çiziyor;
     * kusur duruyor, yalnız görünmez oluyordu. Karar önbellekten okunuyor, gidiş ise
     * ağaç tazeyi görene kadar bekletiliyor.
     */
    const hedef = guvenliDonusYolu(params.get(ISTENEN_DONUS_PARAM)) ?? "/tenants";
    const taze = queryClient.getQueryData<{ user: { emailVerified: boolean } | null }>(["session"]);
    /**
     * Sunucu HÂLÂ "doğrulanmamış" diyorsa bekleyecek bir şey yok: hedef ekranın uyarısı
     * o hâlde DOĞRU bilgidir. Beklemek kullanıcıyı sonsuza kadar tutardı.
     */
    if (!taze?.user?.emailVerified) {
      navigate(hedef);
      return;
    }
    setBekleyenHedef(hedef);
  };

  /**
   * Gidiş, oturum bayrağı AĞACA işlendikten sonra. Zamanlayıcı yok: efekt zaten ancak
   * yeni değerle render olunduktan sonra koşar.
   */
  useEffect(() => {
    if (bekleyenHedef && user?.emailVerified) navigate(bekleyenHedef);
  }, [bekleyenHedef, user?.emailVerified, navigate]);

  // React 19 StrictMode geliştirmede efektleri İKİ KEZ çalıştırır. Koruma olmasaydı
  // token iki kez tüketilmeye çalışılır ve ikincisi "geçersiz" görünürdü — kullanıcı
  // geçerli bir bağlantıyla gelip hata ekranı görürdü.
  const denendi = useRef(false);

  useEffect(() => {
    if (!token || denendi.current) return;
    denendi.current = true;

    void (async () => {
      try {
        await api("/api/session/verify-email", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        // "Doğrulandı" ekranını HEMEN göster — oturumu burada tazelemek (await) ekranı
        // session refetch'i bitene kadar bloklardı ve yavaş/yoğun ağda "başarılı" durumu
        // geç gelirdi. Oturumdaki bayat `emailVerified`, ileri gidilen anda (butonda)
        // tazeleniyor; render bunu beklemiyor.
        setDurum("basarili");
      } catch {
        // Sunucu neden reddetti bilmiyoruz ve BİLMEMELİYİZ; kullanıcıya yapabileceği
        // şeyi gösteriyoruz.
        setDurum("bekliyor");
      }
    })();
  }, [token]);

  const form = useZodForm(resendVerificationSchema, {
    defaultValues: { email: "" } as ResendValues,
  });

  /**
   * Gönderimin TEK yolu.
   *
   * ⚠ Oturumlu akış `form.handleSubmit`'ten GEÇMİYOR ve bu zorunlu: e-posta alanı
   * çizilmediği için `values.email` boş kalıyor, şema doğrulaması isteği hiç göndermiyordu
   * (ölçüldü — buton çalışmıyor görünüyordu). Oturumlu kullanıcının adresini form değil
   * oturum biliyor.
   */
  const gonder = async (email: string) => {
    try {
      const { data } = await api<{ message: string }>("/api/session/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      // Metin SUNUCUDAN gelir. İstemci uydursaydı, sunucunun anti-enumeration ifadesiyle
      // ayrışabilir ve aradaki fark bilgi sızdırabilirdi.
      setGonderimMesaji(data.message);
    } catch (error) {
      applyServerErrors(form, error);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "İşlem tamamlanamadı. Lütfen tekrar deneyin.",
      );
    }
  };

  const onSubmit = form.handleSubmit((values) => gonder(values.email));

  const [gonderiliyor, setGonderiliyor] = useState(false);
  /**
   * BEKLEYEN DAVET ÖZETİ (0A turu dönüşü).
   *
   * ### ⚠ NEDEN UCU KAPATMAK DEĞİL
   *
   * Tur şunu ölçtü: doğrulanmamış kullanıcı panelde yalnız bu ekranı görüyor ama
   * `GET /api/invitations/inbox` 200 + veri dönüyor. İki çözüm vardı: ucu kapatmak ya da
   * bu ekranda özet göstermek. İkincisi seçildi — liste kullanıcının KENDİ davetlerini
   * gösteriyor, kabul/ret sunucuda zaten kapalı, ve ucu kapatmak kullanıcının "bir
   * davetim var, doğrulayınca yanıtlayabilirim" bilgisini de keserdi.
   *
   * ⚠ Sayı SUNUCUDAN (`pendingCount`); ekran kendi saymıyor.
   */
  const davetler = useQuery({
    enabled: Boolean(user) && user?.emailVerified === false,
    queryKey: ["invitations", "inbox", "verify-email"],
    queryFn: async () => (await api<InvitationInbox>("/api/invitations/inbox")).data,
  });

  const oturumluGonder = async () => {
    if (!user) return;
    setGonderiliyor(true);
    try {
      await gonder(user.email);
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <AuthLayout title="E-posta adresinizi doğrulayın" icon={<MailIcon className="size-6" />}>
      {durum === "dogrulaniyor" && (
        <div role="status" className="flex flex-col items-center gap-3 py-4">
          <Spinner size="lg" />
          <p className="text-sm text-fg-muted">Bağlantınız kontrol ediliyor…</p>
        </div>
      )}

      {durum === "basarili" && (
        <div className="flex flex-col gap-4">
          <p role="status" className="text-sm text-fg-muted">
            E-posta adresiniz doğrulandı. Artık hesabınızı kullanmaya başlayabilirsiniz.
          </p>
          {/* Doğrulama KESİN başarılı; GeriDon'un session'a bakan mantığı burada bayat
                bayrak yüzünden yanlış karar verirdi. Girişliyse uygulamaya, değilse girişe. */}
          {user ? (
            <Button variant="ghost" onClick={() => void uygulamayaDevam()}>
              Uygulamaya devam et
            </Button>
          ) : (
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          )}
        </div>
      )}

      {durum === "bekliyor" &&
        (gonderimMesaji ? (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              {gonderimMesaji}
            </p>
            {/* Form KAPANIR: sunucudaki cooldown ile aynı yöne çalışıyoruz, ona karşı
                  değil. Açık kalsaydı kullanıcı tekrar tıklar ve sessizce reddedilirdi. */}
            <GeriDon />
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
            {/*
                ⚠ OTURUM AÇIKKEN E-POSTA SORULMUYOR (CLOS-822).
                Kullanıcı giriş yapmış; adresi zaten biliniyor ve kayıt sırasında bağlantı
                çoktan gönderilmiş. Boş bir alan göstermek, sistemin kendisini tanımadığı
                izlenimi veriyordu — üstelik gövdeye başka bir adres yazma yolunu da açık
                bırakıyordu (kapı artık sunucuda: oturum varsa adres oturumdan alınıyor).
              */}
            {/*
                ⚠ BEKLEYEN DAVET ÖZETİ: doğrulama bir amaç değil, bir engel. Kullanıcı
                niye doğruladığını görsün diye bekleyen davet sayısı burada anılıyor.
              */}
            {davetler.data && davetler.data.pendingCount > 0 ? (
              <p className="text-sm text-fg" data-testid="bekleyen-davet-ozeti">
                Doğruladıktan sonra yanıtlayabileceğiniz {davetler.data.pendingCount} bekleyen
                davetiniz var.
              </p>
            ) : null}

            {user ? (
              <p className="text-sm text-fg-soft">
                {/*
                    ⚠ ADRES MASKELİ (0A turu dönüşü): davet ekranı maskeliyordu, burası düz
                    gösteriyordu — aynı ürün, aynı bilgi, iki farklı dil. Maske "hangi
                    kutuya bakacağım" sorusunu cevaplamaya yetiyor; tam adresi tekrarlamak
                    omuz üstünden okumaya açık bir bilgi.
                  */}
                Doğrulama bağlantısını{" "}
                <span className="font-medium text-fg">{maskEmail(user.email)}</span> adresine
                yeniden gönderelim.
              </p>
            ) : (
              <>
                <p className="text-sm text-fg-soft">
                  {token
                    ? "Bağlantı geçersiz ya da süresi dolmuş. Yeni bir doğrulama bağlantısı isteyin."
                    : "Hesabınızın e-posta adresini girin; doğrulama bağlantısını gönderelim."}
                </p>
                <FormField label="E-posta" error={form.formState.errors.email?.message} required>
                  <Input type="email" autoComplete="email" autoFocus {...form.register("email")} />
                </FormField>
              </>
            )}
            {user ? (
              <Button type="button" loading={gonderiliyor} onClick={() => void oturumluGonder()}>
                Bağlantıyı yeniden gönder
              </Button>
            ) : (
              <Button type="submit" loading={form.formState.isSubmitting}>
                Bağlantıyı yeniden gönder
              </Button>
            )}
            <div className="text-center text-sm">
              <GeriDon className="font-medium text-primary hover:underline" />
            </div>
          </form>
        ))}
    </AuthLayout>
  );
}
